/**
 * The route class of the Microsoft Teams tab (022's tab single sign-on, 2026-10-02): the tab's
 * content URL is `/teams/tab`, and inside the tab the whole application runs under that prefix as
 * its router basename, so `/teams/tab/acme/tasks/BAT-1` is the task page. The tab is known by its
 * route class and by nothing else: not by `window.parent`, because Teams mobile runs a tab at the
 * top level of a webview, and not by the Teams library, which must never load outside the tab
 * (`main.tsx` imports the tab's bootstrap only on this test). The prefix is the build's
 * `TEAMS_TAB_PREFIX` of `vite.config.ts`, repeated here because the entry's initial chunk must
 * not import the build's configuration; `tab.test.ts` holds the two to one value.
 */
export const TEAMS_TAB_PREFIX = "/teams/tab";

/** Whether a path is the tab's root or beneath it, which is what loads the application inside Teams. */
export function inTeamsTab(pathname: string = window.location.pathname): boolean {
  return pathname === TEAMS_TAB_PREFIX || pathname.startsWith(`${TEAMS_TAB_PREFIX}/`);
}

/** Whether a path is the tab's root, with or without a trailing slash: where a deep link's sub-page is read (`deepLink.ts`). */
export function atTeamsTabRoot(pathname: string = window.location.pathname): boolean {
  return pathname.replace(/\/+$/u, "") === TEAMS_TAB_PREFIX;
}

/**
 * The same page outside the tab: the path with the prefix taken off, its query and fragment kept,
 * where a tab path opened in a plain browser is sent so the ordinary application takes over.
 */
export function outsideTeamsTab(location: { readonly pathname: string; readonly search: string; readonly hash: string } = window.location): string {
  const path = location.pathname.slice(TEAMS_TAB_PREFIX.length);
  return `${path.length === 0 ? "/" : path}${location.search}${location.hash}`;
}

/**
 * Where a full navigation goes to sign in again: the public sign-in document outside the tab, and
 * the tab's own root inside it, since Teams cannot frame `/sign-in` and loading the tab signs in
 * through Teams again. Every place that leaves for sign-in (`routes/session.ts`, the sign-out and
 * the dismissed re-authentication dialog) asks this one function rather than naming `/sign-in`.
 */
export function signInEntry(): string {
  return inTeamsTab() ? TEAMS_TAB_PREFIX : "/sign-in";
}
